/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";

const FUEL_TYPE_OPTIONS = [
  "Natural gas",
  "Coal",
  "Petroleum/Gasoline",
  "Diesel",
  "Fuel oil",
  "LPG",
  "Propane",
];

const FUEL_UNIT_BY_TYPE = {
  "Natural gas": ["M3"],
  Coal: ["Tonnes", "Kg"],
  "Petroleum/Gasoline": ["Liters"],
  Diesel: ["Liters"],
  "Fuel oil": ["Liters"],
  LPG: ["Liters", "Tonnes"],
  Propane: ["Kg", "Tonnes"],
};

const unitMenuProps = {
  PaperProps: {
    style: {
      maxHeight: "55%",
      overflowY: "auto",
    },
  },
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  productOptions = [],
  onSearchProducts = () => {},
}) => {
  const toDateInputValue = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

      const dmyMatch = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (dmyMatch) {
        const dd = String(dmyMatch[1]).padStart(2, "0");
        const mm = String(dmyMatch[2]).padStart(2, "0");
        const yyyy = dmyMatch[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const unitOfProductSoldObj = initialData?.unitOfProductSold || {};

  const initialFormData = {
    productMasterId: initialData?.productMasterId || initialData?.productId || "",
    productName: initialData?.productName || "",
    productId: initialData?.productId || initialData?.productId || "",
    productCategory: initialData?.productCategory || "",
    dateOfSale: toDateInputValue(initialData?.dateOfSale),
    unitOfProductSold: unitOfProductSoldObj?.value || "",
    unitOfProductSoldUnit: unitOfProductSoldObj?.unit || "",
    fuelType: initialData?.fuelType || "",
    fuelConsumption: initialData?.fuelConsumption || "",
    fuelConsumptionUnit: initialData?.fuelConsumptionUnit || "",
    productDescription: initialData?.productDescription || "",
    expectedLifetimeOfTheProductYears: initialData?.expectedLifetimeOfTheProductYears || "",
    energyOrFuelUsedForMaintenance:
      initialData?.energyOrFuelUsedForMaintenance ||
      initialData?.energyTypeRequired ||
      initialData?.energyTypeRequiredForUse ||
      "",
    averageAnnualEnergyConsumptionPerUnit:
      initialData?.averageAnnualEnergyConsumptionPerUnit ||
      initialData?.avgAnnualEnergyConsumptionPerUnit ||
      "",
    typicalOperatingHours: initialData?.typicalOperatingHours || "",
    typeOfMaintenanceRequired: initialData?.typeOfMaintenanceRequired || "",
    frequencyOfMaintenancePerYear:
      initialData?.frequencyOfMaintenancePerYear || initialData?.frequencyOfMaintenancePerYear || "",
    recyclableComponentsPercentage:
      initialData?.recyclableComponentsPercentage || initialData?.replacementParts || "",
    designedForReuseRecyclingDisposal:
      initialData?.designedForReuseRecyclingDisposal ||
      initialData?.isProductDesignedForReuseRecyclingDisposal ||
      "",
    averageDisposalMethodByCustomers:
      initialData?.averageDisposalMethodByCustomers || initialData?.disposalMethod || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [productSearchText, setProductSearchText] = useState("");

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setProductSearchText(initialFormData.productName || "");
  }, [isOpen, initialData]);

  const fieldConfig = [
    {
      label: "Product Name",
      name: "productName",
      type: "text",
      required: true,
      placeholder: "Enter product name",
    },
    {
      label: "Product ID / SKU",
      name: "productId",
      type: "text",
      required: true,
      placeholder: "Enter product ID / SKU",
      disabled: true,
    },
    {
      label: "Product Category",
      name: "productCategory",
      type: "text",
      required: true,
      placeholder: "Enter product category",
      disabled: true,
    },
    {
      label: "Product Description",
      name: "productDescription",
      type: "text",
      required: true,
      placeholder: "Enter product description",
      disabled: true,
    },
    {
      label: "Expected Lifetime of the Product (years)",
      name: "expectedLifetimeOfTheProductYears",
      type: "number",
      required: true,
      placeholder: "Enter years",
      disabled: true,
    },
    {
      label: "Date of Sale",
      name: "dateOfSale",
      type: "date",
      required: true,
    },
    {
      label: "Fuel Name",
      name: "fuelType",
      type: "select",
      required: true,
      options: FUEL_TYPE_OPTIONS,
    },
    {
      label: "Fuel Quantity",
      name: "fuelConsumption",
      type: "number",
      required: true,
      placeholder: "Enter consumption",
    },
    {
      label: "Unit of Product Sold (Quantity)",
      name: "unitOfProductSold",
      type: "number",
      required: true,
      placeholder: "Enter quantity",
      options: ["MT","KL","M3"],
      unitFieldName: "unitOfProductSoldUnit",
    },
   
   
    {
      label: "Energy or Fuel Used for Maintenance",
      name: "energyOrFuelUsedForMaintenance",
      type: "text",
      required: true,
      placeholder: "Enter quantity",
      disabled: true,
    },
    {
      label: "Average Annual Energy Consumption per Unit",
      name: "averageAnnualEnergyConsumptionPerUnit",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "Typical Operating Hours per Day / Week / Year",
      name: "typicalOperatingHours",
      type: "text",
      required: true,
      placeholder: "Enter hours",
    },
    {
      label: "Type of Maintenance Required",
      name: "typeOfMaintenanceRequired",
      type: "text",
      required: true,
      placeholder: "Enter maintenance type",
    },
    {
      label: "Frequency of Maintenance per year",
      name: "frequencyOfMaintenancePerYear",
      type: "text",
      required: true,
      placeholder: "Enter frequency",
      disabled: true,
    },
    {
      label: "Recyclable Components (%)",
      name: "recyclableComponentsPercentage",
      type: "text",
      required: true,
      placeholder: "Enter recyclable components",
      // disabled: true,
    },
    {
      label: "Is the Product Designed for Reuse, Recycling, or Disposal?",
      name: "designedForReuseRecyclingDisposal",
      type: "text",
      // required: true,
      placeholder: "Enter details",
    },
    {
      label: "Average Disposal Method by Customers",
      name: "averageDisposalMethodByCustomers",
      type: "text",
      required: true,
      placeholder: "Enter disposal method",
      disabled: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field.name];

      if (field.required) {
        if (value === "" || value === null || value === undefined) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if (field.unitFieldName) {
        const unitVal = formData[field.unitFieldName];
        if (unitVal === "" || unitVal === null || unitVal === undefined) {
          newErrors[field.unitFieldName] = `${field.label} unit is required.`;
        }
      }

      if (
        field.name === "fuelConsumption" &&
        (formData.fuelConsumptionUnit === "" ||
          formData.fuelConsumptionUnit === null ||
          formData.fuelConsumptionUnit === undefined)
      ) {
        newErrors.fuelConsumptionUnit = "Fuel Quantity unit is required.";
      }

      if (field.type === "number" && value !== "" && isNaN(Number(value))) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    if (name === "fuelType") {
      setFormData((prev) => ({
        ...prev,
        fuelType: value,
        fuelConsumptionUnit: "",
      }));
      setErrors((prev) => ({
        ...prev,
        fuelType: "",
        fuelConsumptionUnit: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const resolveProductField = (selected, keys = []) => {
    for (const key of keys) {
      if (selected?.[key] !== undefined && selected?.[key] !== null) {
        return selected[key];
      }
    }
    return "";
  };

  const applySelectedProduct = (selected) => {
    if (!selected) return;

    setFormData((prev) => ({
      ...prev,
      productMasterId: resolveProductField(selected, ["_id", "id"]),
      productName: resolveProductField(selected, ["productName", "name"]),
      productId: resolveProductField(selected, ["productId", "productId", "sku"]),
      productCategory: resolveProductField(selected, ["productCategory", "category"]),
      productDescription: resolveProductField(selected, ["productDescription", "description"]),
      expectedLifetimeOfTheProductYears: resolveProductField(selected, ["expectedLifetimeOfTheProductYears"]),
      energyOrFuelUsedForMaintenance: resolveProductField(selected, [
        "energyOrFuelUsedForMaintenance",
        "energyTypeRequired",
        "energyTypeRequiredForUse",
      ]),
      averageAnnualEnergyConsumptionPerUnit: resolveProductField(selected, [
        "averageAnnualEnergyConsumptionPerUnit",
        "avgAnnualEnergyConsumptionPerUnit",
      ]),
      typicalOperatingHours: resolveProductField(selected, ["typicalOperatingHours"]),
      typeOfMaintenanceRequired: resolveProductField(selected, ["typeOfMaintenanceRequired"]),
      frequencyOfMaintenancePerYear: resolveProductField(selected, ["frequencyOfMaintenancePerYear"]),
      recyclableComponentsPercentage: resolveProductField(selected, [
        "recyclableComponentsPercentage",
        "replacementParts",
      ]),
      designedForReuseRecyclingDisposal: resolveProductField(selected, [
        "designedForReuseRecyclingDisposal",
        "isProductDesignedForReuseRecyclingDisposal",
      ]),
      averageDisposalMethodByCustomers: resolveProductField(selected, [
        "disposalMethod",
        "averageDisposalMethodByCustomers",
      ]),
    }));

    setErrors((prev) => ({
      ...prev,
      productName: "",
      productId: "",
      productCategory: "",
      productDescription: "",
      expectedLifetimeOfTheProductYears: "",
      energyOrFuelUsedForMaintenance: "",
      averageAnnualEnergyConsumptionPerUnit: "",
      typicalOperatingHours: "",
      typeOfMaintenanceRequired: "",
      frequencyOfMaintenancePerYear: "",
      recyclableComponentsPercentage: "",
      designedForReuseRecyclingDisposal: "",
      averageDisposalMethodByCustomers: "",
    }));
  };

  const clearAutoFilledProductData = () => {
    setFormData((prev) => ({
      ...prev,
      productMasterId: "",
      productName: "",
      productId: "",
      productCategory: "",
      productDescription: "",
      expectedLifetimeOfTheProductYears: "",
      energyOrFuelUsedForMaintenance: "",
      averageAnnualEnergyConsumptionPerUnit: "",
      typicalOperatingHours: "",
      typeOfMaintenanceRequired: "",
      frequencyOfMaintenancePerYear: "",
      recyclableComponentsPercentage: "",
      designedForReuseRecyclingDisposal: "",
      averageDisposalMethodByCustomers: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    
    let formDataToSubmit = {
      ...formData,
      documents: uploadedFiles,
    };
    
    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : field.name === "productName" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={productOptions}
                      value={
                        productOptions.find(
                          (option) =>
                            (option?._id || option?.id) === formData.productMasterId ||
                            (option?.productName || option?.name) === formData.productName
                        ) || null
                      }
                      inputValue={productSearchText}
                      onInputChange={(event, newInputValue, reason) => {
                        setProductSearchText(newInputValue);
                        if (reason === "input" || reason === "clear") {
                          onSearchProducts(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          applySelectedProduct(newValue);
                        } else {
                          clearAutoFilledProductData();
                        }
                        setProductSearchText(
                          newValue?.productName || newValue?.name || ""
                        );
                      }}
                      getOptionLabel={(option) =>
                        option?.productName || option?.name || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        (option?._id || option?.id) === (value?._id || value?.id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Tap to Select"
                          error={Boolean(errors[field.name])}
                          helperText={errors[field.name] || ""}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-inputRoot": {
                          height: 40,
                          alignItems: "center",
                        },
                      }}
                    />
                  </>
                ) : field?.type === "select" && field?.name === "fuelType" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      required={field.required}
                      value={formData[field.name] || ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value="" disabled>
                        Tap to select
                      </MenuItem>
                      {FUEL_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                ) : field?.name === "fuelConsumption" ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                      {field?.label}
                    </Typography>
                    <Box display="flex" gap={1} sx={{ width: "100%" }}>
                      <Box sx={{ width: "60%" }}>
                        <TextField
                          type="text"
                          size="small"
                          fullWidth
                          placeholder={field?.placeholder || ""}
                          value={formData.fuelConsumption || ""}
                          onChange={handleChange("fuelConsumption", "number")}
                          error={Boolean(errors.fuelConsumption)}
                          helperText={errors.fuelConsumption || ""}
                        />
                      </Box>
                      <Box sx={{ width: "40%" }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          name="fuelConsumptionUnit"
                          value={formData.fuelConsumptionUnit || ""}
                          onChange={handleChange("fuelConsumptionUnit", "select")}
                          disabled={!formData.fuelType}
                          error={Boolean(errors.fuelConsumptionUnit)}
                          SelectProps={{ MenuProps: unitMenuProps, displayEmpty: true }}
                        >
                          <MenuItem value="" disabled>
                            Unit
                          </MenuItem>
                          {(FUEL_UNIT_BY_TYPE[formData.fuelType] || []).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </TextField>
                        {errors.fuelConsumptionUnit && (
                          <Typography variant="body2" color="#D34040">
                            {errors.fuelConsumptionUnit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>

                    {field.unitFieldName ? (
                  <Box sx={{ display: "flex", gap: "0.75rem" }}>
                    <TextField
                      type={"text"}
                      size="small"
                      fullWidth
                      required={field.required}
                      placeholder={field.placeholder || ""}
                      value={formData[field.name] || ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                    />
                    <TextField
                      select
                      size="small"
                      sx={{ width: "42%" }}
                      required
                      value={formData[field.unitFieldName] || ""}
                      onChange={handleChange(field.unitFieldName, "select")}
                      error={Boolean(errors[field.unitFieldName])}
                      helperText={errors[field.unitFieldName] || ""}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>
                        Unit
                      </MenuItem>
                      {field.options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ) : field.type === "select" ? (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    required={field.required}
                    value={formData[field.name] || ""}
                    onChange={handleChange(field.name, field.type)}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ""}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Tap to select
                    </MenuItem>
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    type={field.type === "date" ? "date" : "text"}
                    size="small"
                    fullWidth
                    required={field.required}
                    placeholder={field.placeholder || ""}
                    value={formData[field.name] || ""}
                    onChange={handleChange(field.name, field.type)}
                      disabled={Boolean(field.disabled)}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ""}
                      sx={{
                        ...(field?.disabled && {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                            cursor: "not-allowed",
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#f3f4f6",
                          },
                        }),
                      }}
                    inputProps={
                      field.type === "date"
                        ? { max: new Date().toISOString().split("T")[0] }
                        : {}
                    }
                  />
                    )}
                  </>
                )}
              </div>
            ))}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

